import { CommonModule, NgFor, NgSwitch } from '@angular/common'
import { Component, inject } from '@angular/core'
import { Store } from '@ngrx/store'
import { HasPermissionDirective } from '../../../../directives/has-permission.directive'
import { StandardEarningsComponent } from '../standard-earnings/standard-earnings.component'
import { CustomEarningsComponent } from '../custom-earnings/custom-earnings.component'
import { CustomDeductionsComponent } from '../custom-deductions/custom-deductions.component'
import { ReimbursementsComponent } from '../reimbursements/reimbursements.component'
import { StatutoryDeductionsComponent } from '../statutory-deductions/statutory-deductions.component'
import { selectUserPermissions } from '../../../../store/auth/auth.selectors'
import { filter, take } from 'rxjs'

@Component({
  selector: 'app-wrapper',
  imports: [
    NgFor,
    NgSwitch,
    CommonModule,
    HasPermissionDirective,
    StandardEarningsComponent,
    StatutoryDeductionsComponent,
    CustomEarningsComponent,
    CustomDeductionsComponent,
    ReimbursementsComponent
  ],
  templateUrl: './wrapper.component.html',
  styleUrl: './wrapper.component.scss'
})
export class WrapperComponent {
  private store = inject(Store)

  tabs: string[] = [
    'Standard Earnings',
    'Custom Earnings',
    'Statutory Deductions',
    'Custom Deductions',
    'Reimbursements'
  ]

  // Explicit tab -> moduleName mapping.
  // "Standard Earnings" ka backend moduleName "earnings" hai, "standardEarnings" nahi —
  // isliye auto camelCase conversion pe bharosa nahi kar sakte.
  private tabModuleMap: Record<string, string> = {
    'Standard Earnings': 'standardEarnings',
    'Custom Earnings': 'customEarnings',
    'Statutory Deductions': 'statutoryDeductions', // apne actual moduleName se confirm kar lena
    'Custom Deductions': 'customDeductions',
    Reimbursements: 'reimbursements'
  }

  //---State---//
  activeChild = '' // Initially empty
  permissionsMap = new Map<string, Record<string, string[]>>()

  constructor() {
    this.store
      .select(selectUserPermissions)
      .pipe(
        // selector kabhi {} de sakta hai (store aur localStorage dono empty hote hue),
        // usse pehli hi emission pe wrong decision na ho isliye non-empty tak wait karo
        filter(userPermissions => {
          if (Array.isArray(userPermissions)) return userPermissions.length > 0
          return !!userPermissions && Object.keys(userPermissions).length > 0
        }),
        take(1)
      )
      .subscribe(userPermissions => {
        const permissionsMap = this.toPermissionsMap(userPermissions)

        const firstAccessibleTab = this.tabs.find(tab => {
          const moduleKey = this.getTabKey(tab)
          return permissionsMap[moduleKey]?.includes('view')
        })

        this.activeChild = firstAccessibleTab ?? this.tabs[0]
      })
  }

  renderChild(tab: string) {
    this.activeChild = tab
  }

  //Creates permissions object (used by *appHasPermission directive)
  makePermissions(tab: string, permissions: string[]): Record<string, string[]> {
    if (!this.permissionsMap.has(tab)) {
      const key = this.getTabKey(tab)
      this.permissionsMap.set(tab, { [key]: [...permissions] })
    }
    return this.permissionsMap.get(tab) ?? {}
  }

  private getTabKey(tab: string): string {
    return (
      this.tabModuleMap[tab] ??
      tab
        .replace(/\s(.)/g, (match, group1) => group1.toUpperCase())
        .replace(/\s/g, '')
        .replace(/^(.)/, match => match.toLowerCase())
    )
  }

  // selectUserPermissions kabhi array ({moduleName, permissions}[]) deta hai
  // (jab store me raw data ho), kabhi normalized Record<string, string[]>
  // (jab localStorage fallback chale). Yahi ambiguity component-level pe handle karo.
  private toPermissionsMap(userPermissions: unknown): Record<string, string[]> {
    if (Array.isArray(userPermissions)) {
      const map: Record<string, string[]> = {}
      userPermissions.forEach((p: any) => {
        if (p?.moduleName) {
          map[p.moduleName] = p.permissions || []
        }
      })
      return map
    }

    return (userPermissions as Record<string, string[]>) ?? {}
  }
}
